import type { JSX, ReactNode } from 'react'

/**
 * A verdict badge: the tone's glyph, then the word.
 *
 * **The glyph is the mockup's own convention, and it is narrower than it looks.**
 * `design/screens/outreach.html` puts a mark on every badge that carries a
 * *judgement* — `✓ Good`, `⚠ Near daily limit`, `✓ SPF`, `✓ Healthy` — and none on
 * a badge that carries a *state*: `Not connected`, `Low volume`, `Contacted`,
 * `Found`, `Encrypted`. So the glyph is keyed on the tone rather than passed in,
 * which makes the rule impossible to apply inconsistently: a neutral badge is a
 * statement of fact and gets nothing, and the three coloured tones each get their
 * mark. OUTR-41, OUTR-42 and OUTR-50 are all the same missing mark.
 *
 * **It earns its place beyond fidelity.** Tone is colour, and a red badge and an
 * amber one are the same badge to a good many readers — which is the argument
 * `AccountHealthPanel` and `DeliverabilityPanel` already make for always pairing a
 * badge with a sentence. A glyph is a second, non-colour channel for the same
 * distinction, on the badge itself.
 *
 * **`aria-hidden` on the mark**, because a screen reader announcing *"check mark
 * Good"* is worse than *"Good"* — the mark repeats the tone, and the tone is
 * already repeated by the word. `TeamPanel.tsx:474` and `AcceptInvitation.tsx:94`
 * do the same with theirs.
 *
 * **An unknown tone renders as a plain badge rather than as nothing.** `tone`
 * arrives as a string from three different endpoints, so a value added to
 * `app/outreach/health.Tone` in a later release reaches this component before this
 * file knows about it — the same fallback, and the same reason, as
 * `DeliverabilityPanel`'s own `tone()`.
 */
const GLYPH: Record<string, string> = {
  success: '✓',
  warning: '⚠',
  // Not `⚠` again: `danger` is a state that has already stopped something —
  // *Restricted*, *Over the limit*, *Sending stopped* — and the two must not
  // wear the same mark when the colour is the only other thing telling them
  // apart. `ui/states.tsx` uses `✕` for exactly this and it is the one already
  // in the product.
  danger: '✕',
}

export function Verdict({ tone, children }: { tone: string; children: ReactNode }): JSX.Element {
  const glyph = GLYPH[tone]
  return (
    <span className={`ar-badge ar-badge--${tone}`}>
      {glyph === undefined ? null : (
        <>
          <span aria-hidden="true">{glyph}</span>{' '}
        </>
      )}
      {children}
    </span>
  )
}
